import { useMemo, useState } from "react";
import { ApiError, apiFetch } from "@/lib/apiClient";
import { clearAuthStorage, setAuthItem } from "@/lib/authStorage";
import { env } from "@/lib/env";
import type {
  UserProfile,
  TrialStatus,
  PendingInvite,
  ContainerAccess,
} from "@/types/session";
import { PlanPicker } from "./PlanPicker";
import { TeamJoinGate } from "./TeamJoinGate";
import { CloseButton } from "./CloseButton";
import { ExportMyDataButton } from "./ExportMyDataButton";
import { FeatureList } from "./FeatureList";
import { formatCurrency } from "./formatCurrency";
import { useWorkspacePlanTier } from "@/features/billing/hooks/useWorkspacePlanTier";
import { useSession } from "@/features/auth/components/SessionProvider";
import { clearUserSpecificStorage } from "@/features/auth/lib/session-storage";

type GateMode =
  | "team_join_terms"
  | "free_terms"
  | "capacity_full"
  | "checkout"
  | "zero_workspace";

interface PlanGateProps {
  user: UserProfile;
  authToken: string | null;
  /** Trial status from auth endpoint */
  trialStatus?: TrialStatus | null;
  /** Whether trial system is at capacity */
  isAtCapacity?: boolean;
  /** Pending team-workspace invites; if non-empty, the team-join gate fires before all other modes (#1776) */
  pendingInvites?: PendingInvite[];
  /**
   * True when the user has a current OR historical team membership (i.e. any
   * workspace they don't own). Combined with `pendingInvites`, this identifies
   * a user with team history who must NEVER be force-ToS'd out of the app by a
   * terms gate. Only a brand-new NON-invited free signup (no invites, no
   * memberships) keeps the non-returnable force-ToS behavior. Whether closing
   * actually returns to the app vs. logs out is now decided by the presence of
   * another usable container (see `fallbackContainer` below) — a suppressed
   * invitee with NO other workspace has nothing to land on and is logged out.
   */
  hasTeamMembership?: boolean;
  /** Called when user voluntarily dismisses (checkout / team-join modes). Hides gate without page reload. */
  onDismiss?: () => void;
  /**
   * Standalone zero-workspace mode (Teams S4 / PDR 006 D4). When true, the user
   * is authenticated but belongs to NO workspace and has no pending invite — a
   * VALID state. The gate renders a "create your Free workspace" surface and the
   * user is NEVER logged out. Wins over every other gate mode.
   */
  zeroWorkspace?: boolean;
}

/**
 * Standalone authenticated zero-workspace surface (Teams S4 / PDR 006 D4).
 * Lets a logged-in user with no workspace create a Free one (POST /workspaces
 * plan:'free') and select it. No close/logout path — the user keeps their
 * session and either creates a workspace or waits for an invite.
 */
function ZeroWorkspaceGate({ authToken }: { authToken: string | null }) {
  const { selectContainer } = useSession();
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Please name your workspace to continue.");
      return;
    }
    if (!authToken) {
      setError("Your session expired—please log in again to continue.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const created = await apiFetch<{ id?: string }>("workspaces", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: trimmed, plan: "free" }),
        authToken,
      });
      if (!created?.id) {
        setError("Could not create your workspace. Please try again.");
        setSubmitting(false);
        return;
      }
      // selectContainer POSTs /auth/select-container and reloads on success,
      // landing the user in their new workspace. Keep the loading state through
      // the reload.
      await selectContainer(created.id);
    } catch (thrown) {
      if (thrown instanceof ApiError) {
        const payload = thrown.payload as { error?: string } | undefined;
        setError(
          payload?.error ??
            "Could not create your workspace. Please try again.",
        );
      } else {
        setError("Could not create your workspace. Please try again.");
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-[#f8f7f5] px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mb-4 inline-flex items-center">
            <img src="/favicon.svg" alt="XAVIOR" className="h-12 w-12" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">
            Create your workspace
          </h1>
          <p className="mt-2 text-gray-600">
            You're not in any workspace yet. Create a free one to get started,
            or wait for a team invitation.
          </p>
        </div>

        <form
          onSubmit={handleCreate}
          className="rounded-xl border border-gray-200 bg-white p-6"
        >
          <label
            className="block text-left text-sm font-medium text-gray-700"
            htmlFor="zero-ws-name"
          >
            Workspace name
          </label>
          <input
            id="zero-ws-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="My Workspace"
            autoFocus
            className="mt-2 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
          />

          {error && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-center text-sm text-red-600">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !name.trim()}
            className="mt-6 w-full rounded-lg bg-sky-500 px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-sky-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "Creating…" : "Create Free Workspace"}
          </button>

          <p className="mt-4 text-center text-xs text-gray-500">
            Free Personal plan — 14 days free, no credit card required.{" "}
            <a
              href="mailto:support@xavior.ai"
              className="text-sky-600 hover:underline"
            >
              Questions?
            </a>
          </p>
        </form>
      </div>
    </div>
  );
}

/**
 * Pick the container to return to when the user closes a gate (#1776). A
 * pending invite is NOT in `containers` (membership only exists after accepting),
 * so every entry here is already a usable workspace BESIDES the pending invite.
 * Prefer an owned container, else fall back to any member one.
 */
function pickFallbackContainer(
  containers: ContainerAccess[],
): ContainerAccess | null {
  if (containers.length === 0) return null;
  return containers.find((c) => c.isOwner) ?? containers[0];
}

async function handleLogout(authToken?: string | null) {
  // Revoke server-side FIRST, then clear local storage. apiClient's logout path
  // reads the native iOS Keychain refresh token (getRefreshTokenSecure) to attach
  // X-Refresh-Token so the server revokes it; clearAuthStorage() (Ch.2) purges
  // that same Keychain token, so clearing FIRST would strip it before the logout
  // POST could send it and the refresh token would never be revoked server-side.
  // Order mirrors WorkspaceOptionsModal (logout, then clear). native-oauth-pass Ch.2 review fix.
  try {
    await apiFetch("/auth/logout", {
      method: "POST",
      authToken: authToken || undefined,
    });
  } catch {
    // Best-effort — clear + navigate to login regardless
  }
  // Purge user-specific caches (incl. email folder/inbox sessionStorage — same
  // identity-leak class as #5276) before the redirect; ordering mirrors
  // SessionProvider's logout (clearUserSpecificStorage, then clearAuthStorage).
  clearUserSpecificStorage();
  clearAuthStorage();
  window.location.href = "/login";
}

function ConfirmExit({
  onGoBack,
  authToken,
}: {
  onGoBack: () => void;
  authToken?: string | null;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f8f7f5] px-4 py-12">
      <div className="w-full max-w-md text-center">
        <div className="rounded-xl border border-gray-200 bg-white p-8">
          <h2 className="text-xl font-bold text-gray-900">
            Are you sure you want to leave?
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            You'll be logged out of Xavior.
          </p>
          <div className="mt-6 flex gap-3 justify-center">
            <button
              type="button"
              onClick={onGoBack}
              className="rounded-lg bg-gray-100 px-6 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200"
            >
              Go Back
            </button>
            <button
              type="button"
              onClick={() => handleLogout(authToken)}
              className="rounded-lg bg-red-500 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-600"
            >
              Log Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PlanGate({
  user,
  authToken,
  trialStatus,
  isAtCapacity,
  pendingInvites,
  hasTeamMembership,
  onDismiss,
  zeroWorkspace,
}: PlanGateProps) {
  const workspacePlanTier = useWorkspacePlanTier();
  // Read containers + selection from the session so close ("X") can route an
  // invitee back into an existing workspace instead of logging them out (#1776).
  const { containers, selectedContainer, selectContainer } = useSession();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showConfirmExit, setShowConfirmExit] = useState(false);

  const PROFESSIONAL_PRICE = 29;

  // Oldest pending invite first — matches the spec's "one gate per team,
  // ordered by invite timestamp" decision (#1776 §06).
  const oldestPendingInvite = useMemo<PendingInvite | null>(() => {
    if (!pendingInvites || pendingInvites.length === 0) return null;
    return [...pendingInvites].sort(
      (a, b) => (a.invitedAt ?? 0) - (b.invitedAt ?? 0),
    )[0];
  }, [pendingInvites]);

  // An invitee (pending invite) or a current/historical team member has team
  // history, so a terms gate must not force-ToS them. Only a brand-new
  // NON-invited free signup (no invites, no memberships) keeps the
  // non-returnable force-ToS flow.
  const isInvitedOrMember =
    (pendingInvites?.length ?? 0) > 0 || Boolean(hasTeamMembership);

  // The workspace to return to when the user closes the gate (prefer owned,
  // else any member one). Null when the user has no usable workspace besides
  // the pending invite — in that case there is nothing to land on, so closing
  // logs out. (Pending invites are never in `containers`; membership only
  // exists after POST /workspaces/invites/:id/accept.)
  const fallbackContainer = useMemo<ContainerAccess | null>(
    () => pickFallbackContainer(containers ?? []),
    [containers],
  );
  const hasOtherUsableContainer = fallbackContainer !== null;

  const getGateMode = (): GateMode => {
    // Standalone zero-workspace mode wins over every other gate (Teams S4 / PDR
    // 006 D4): the user has no workspace to land on and must create a Free one
    // (or wait for an invite) — never logged out.
    if (zeroWorkspace) return "zero_workspace";
    if (isAtCapacity) return "capacity_full";
    // Pending team-workspace invite always wins over all other modes — the
    // user is mid-onboarding into a team and shouldn't see the personal
    // free_terms / checkout flow until they decide on the invite. (#1776)
    if (oldestPendingInvite) return "team_join_terms";
    // Free-plan default (M1 #1755 retired the 7-day trial signup path).
    // Suppressed when versioned consent is live (TC_VERSIONED_CONSENT ON,
    // signalled by a non-`flag_off` reason): the VersionedTermsGate (#4204) is
    // then the sole consent authority and runs upstream in App's gate chain, so
    // the legacy free_terms welcome gate must not also fire.
    const versionedConsentActive =
      user?.requiresConsent != null &&
      user.requiresConsent.reason !== "flag_off";
    if (
      !versionedConsentActive &&
      workspacePlanTier !== null &&
      workspacePlanTier === "free" &&
      !user?.agreementsAcceptedAt
    )
      return "free_terms";
    // Legacy trial-expired users (pre-#1755 model) route into the standard
    // 3-tier checkout picker — the trial system was retired in favor of
    // Free / Professional / Teams, so they pick a plan like anyone else.
    return "checkout";
  };

  const gateMode = getGateMode();
  const showPricing = gateMode !== "free_terms";

  // Whether closing ("X") returns to the app vs. logs out. The terms-history
  // modes (team_join_terms / free_terms-for-an-invitee / capacity_full-for-an-
  // invitee) can only return when the user has ANOTHER usable workspace to land
  // on — a suppressed invitee with zero other workspaces has nothing to return
  // to, so closing logs them out (the correct outcome, NOT the old bug).
  //
  // checkout = trial-expiry wall / upgrade picker — always has a dismiss path.
  // team_join_terms = pending team invite → return to an existing workspace if
  //                   one exists (NEVER auto-join; membership only exists after
  //                   accepting), else log out.
  // free_terms = brand-new NON-invited free signup must accept terms → can't go
  //              back. An invitee/member with another workspace returns to it.
  // capacity_full = normally confirm-exit then log out, EXCEPT an invitee/member
  //                 who has another workspace, who is returned to it instead.
  const canReturnToApp =
    gateMode === "checkout" ||
    (gateMode === "team_join_terms" && hasOtherUsableContainer) ||
    ((gateMode === "free_terms" || gateMode === "capacity_full") &&
      isInvitedOrMember &&
      hasOtherUsableContainer);

  function handleClose() {
    if (!canReturnToApp) {
      // No usable workspace to land on (or a non-returnable force-ToS signup):
      // confirm, then log out.
      setShowConfirmExit(true);
      return;
    }

    // Non-destructive close. Dismiss first so the session dismiss flag is set
    // before any reload selectContainer triggers (keeps the gate from re-showing).
    if (onDismiss) onDismiss();

    // checkout is already on the user's selected container — just hand back to
    // App. For the terms-history modes, an invitee's fallback workspace may not
    // be the (possibly null) currently-selected one, so switch to it. If it is
    // already selected, the dismiss above is enough.
    if (gateMode !== "checkout" && fallbackContainer) {
      const alreadySelected = selectedContainer?.id === fallbackContainer.id;
      if (!alreadySelected) {
        // selectContainer POSTs /auth/select-container and reloads on success.
        void selectContainer(fallbackContainer.id);
        return;
      }
    }

    if (!onDismiss) window.location.href = "/";
  }

  async function initiateCheckout() {
    const response = await apiFetch<{ url?: string; sessionId?: string }>(
      "payment/create-checkout",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan: "professional" }),
        authToken,
      },
    );

    if (response?.url) {
      window.location.href = response.url;
      return;
    }

    if (response?.sessionId) {
      window.location.href = `https://checkout.stripe.com/pay/${response.sessionId}`;
      return;
    }

    setError("Unable to prepare checkout. Please try again.");
    setSubmitting(false);
  }

  async function handleAction() {
    if (!authToken) {
      setError("Your session expired—please log in again to continue.");
      return;
    }

    if (!agreedToTerms) {
      setError(
        "Please agree to the Terms of Service and Privacy Policy to continue.",
      );
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      if (gateMode === "free_terms") {
        const response = await apiFetch<{
          success: boolean;
          accessToken?: string;
          error?: string;
        }>("auth/accept-agreements", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          authToken,
        });

        if (response?.success) {
          if (response.accessToken) {
            // Persist via setAuthItem so the rotated token lands in the
            // platform-canonical store (localStorage on native, sessionStorage on
            // web). A raw sessionStorage write was read back as stale on native
            // after the reload below, since the reader now uses localStorage.
            setAuthItem("xavior_auth_token", response.accessToken);
          }
          window.location.reload();
          return;
        }

        setError(
          response?.error ?? "Failed to record acceptance. Please try again.",
        );
        setSubmitting(false);
        return;
      }

      // Checkout redirects to Stripe — keep loading state
      await initiateCheckout();
    } catch (thrown) {
      if (thrown instanceof ApiError) {
        const payload = thrown.payload as { error?: string } | undefined;
        setError(payload?.error ?? "Request failed. Please try again.");
      } else {
        setError("Request failed. Please try again.");
      }
      setSubmitting(false);
    }
  }

  async function handleWaitlist(e: React.FormEvent) {
    e.preventDefault();
    if (!waitlistEmail || !waitlistEmail.includes("@")) return;

    try {
      await fetch(`${env.authBaseUrl}/auth/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: waitlistEmail }),
      });
    } catch {
      // Show success regardless — don't block the user
    }
    setWaitlistSubmitted(true);
  }

  // Standalone zero-workspace surface (Teams S4 / PDR 006 D4) — render before any
  // confirm-exit/logout path so a workspace-less user is never logged out.
  if (gateMode === "zero_workspace") {
    return <ZeroWorkspaceGate authToken={authToken} />;
  }

  if (showConfirmExit) {
    return (
      <ConfirmExit
        onGoBack={() => setShowConfirmExit(false)}
        authToken={authToken}
      />
    );
  }

  // Team-workspace welcome gate (#1776) — fires before any other paywall surface
  // when the user has a pending invite to a team workspace.
  if (gateMode === "team_join_terms" && oldestPendingInvite) {
    return (
      <div className="relative flex min-h-screen items-center justify-center bg-[#f8f7f5] px-4 py-12">
        <CloseButton onClick={handleClose} />
        <TeamJoinGate
          invite={oldestPendingInvite}
          authToken={authToken}
          onError={setError}
        />
        {error && (
          <div className="fixed bottom-8 left-1/2 z-10 -translate-x-1/2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-center text-sm text-red-600">
            {error}
          </div>
        )}
      </div>
    );
  }

  // 3-tier picker (Free / Pro / Team) is the primary paywall surface for:
  //   - free_terms: brand-new free signups + migrated trial users (no agreements yet)
  //   - checkout:   existing users clicking Upgrade (?upgrade=1), and legacy
  //                 trial-expired users (post-M1 #1755 trial retirement)
  // capacity_full keeps its legacy single-pane UI.
  if (gateMode === "free_terms" || gateMode === "checkout") {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-[#f8f7f5] xv-scroll">
        <div className="relative flex min-h-full items-start justify-center px-4 py-8 md:items-center md:py-12">
          <CloseButton onClick={handleClose} />
          <PlanPicker
            user={user}
            authToken={authToken}
            onError={setError}
            context={gateMode === "free_terms" ? "first_login" : "upgrade"}
            onDismiss={gateMode === "checkout" ? onDismiss : undefined}
          />
        </div>
        {/* Locked-window "Export my data" (PDR 011 §6, #4206) — shown to a
            lapsed/upgrading user so they can take their data before the day-21
            delete. Not on first-login (free_terms), where there's nothing yet. */}
        {gateMode === "checkout" && (
          <div className="fixed bottom-6 left-1/2 z-10 -translate-x-1/2">
            <ExportMyDataButton authToken={authToken} />
          </div>
        )}
        {error && (
          <div className="fixed bottom-8 left-1/2 z-20 -translate-x-1/2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-center text-sm text-red-600">
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-[#f8f7f5] px-4 py-12">
      <CloseButton onClick={handleClose} />
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mb-4 inline-flex items-center">
            <img src="/favicon.svg" alt="XAVIOR" className="h-12 w-12" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">
            {gateMode === "free_terms" && "Welcome to XAVIOR"}
            {gateMode === "capacity_full" && "We're at Capacity"}
            {gateMode === "checkout" && "Upgrade to Professional"}
          </h1>
          <p className="mt-2 text-gray-600">
            {gateMode === "free_terms" &&
              "You're on the Free Personal plan — a private workspace, free for 14 days. Accept the terms to continue."}
            {gateMode === "capacity_full" &&
              "Join the waitlist and we'll notify you when a spot opens up."}
            {gateMode === "checkout" &&
              "Get full access to XAVIOR with Professional"}
          </p>
        </div>

        {gateMode === "capacity_full" ? (
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <FeatureList />

            <div className="mt-6">
              {waitlistSubmitted ? (
                <div className="rounded-lg border border-green-200 bg-green-50 px-6 py-4 text-center">
                  <p className="font-medium text-green-800">
                    You're on the waitlist!
                  </p>
                  <p className="mt-1 text-sm text-green-600">
                    We'll notify you when a spot opens up.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleWaitlist}>
                  <p className="mb-4 text-sm text-gray-600 text-center">
                    We're currently at capacity. Leave your email and we'll let
                    you know when a trial slot opens up.
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={waitlistEmail}
                      onChange={(e) => setWaitlistEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                    />
                    <button
                      type="submit"
                      className="rounded-lg bg-sky-500 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-sky-600"
                    >
                      Join Waitlist
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Plan Card — skipped for free_terms (minimal layout: subtitle + checkbox + CTA) */}
            {gateMode !== "free_terms" && (
              <div className="rounded-xl border border-gray-200 bg-white p-6">
                {showPricing && (
                  <>
                    <div className="mb-2 text-sm font-medium text-sky-600">
                      Professional
                    </div>
                    <div className="mb-4">
                      <span className="text-4xl font-bold text-gray-900">
                        {formatCurrency(PROFESSIONAL_PRICE)}
                      </span>
                      <span className="text-gray-500">/month</span>
                    </div>
                  </>
                )}

                <FeatureList />

                {showPricing && (
                  <div className="mt-6 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-3">
                    <p className="text-sm text-gray-500">
                      <span className="font-medium text-gray-700">
                        Add additional workspaces
                      </span>{" "}
                      &mdash; Coming Soon!
                    </p>
                  </div>
                )}
              </div>
            )}

            {error && (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-center text-sm text-red-600">
                {error}
              </div>
            )}

            <div className="mt-6">
              <label className="mb-4 flex items-start justify-center gap-3 text-left">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                />
                <span className="text-sm text-gray-600">
                  I agree to the{" "}
                  <a
                    href={env.termsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-600 hover:underline"
                  >
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a
                    href={env.privacyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-600 hover:underline"
                  >
                    Privacy Policy
                  </a>
                </span>
              </label>

              <button
                type="button"
                onClick={handleAction}
                disabled={submitting || !agreedToTerms}
                className="w-full rounded-lg bg-sky-500 px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-sky-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting
                  ? gateMode === "free_terms"
                    ? "Continuing..."
                    : "Redirecting to checkout..."
                  : gateMode === "free_terms"
                    ? "Continue to XAVIOR"
                      : `Upgrade Now — ${formatCurrency(PROFESSIONAL_PRICE)}/mo`}
              </button>

              <p className="mt-4 text-center text-xs text-gray-500">
                {gateMode === "free_terms" ? (
                  <>
                    Free Personal plan — 14 days free, no credit card required.{" "}
                    <a
                      href="mailto:support@xavior.ai"
                      className="text-sky-600 hover:underline"
                    >
                      Questions?
                    </a>
                  </>
                ) : (
                  <>
                    Cancel anytime. Questions?{" "}
                    <a
                      href="mailto:support@xavior.ai"
                      className="text-sky-600 hover:underline"
                    >
                      support@xavior.ai
                    </a>
                  </>
                )}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
